import { StyleType, CssColor, wixColor } from '@wix/editor-platform-ooi-metadata-resolver';
import { StyleParamType, wixColorParam, wixFontParam, IWixStyleFont, createStylesParam } from '@wix/tpa-settings';

const titleFont = (key: string) =>
  createStylesParam(key, {
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      const { value: _, ...font } = wixFontParam('Body-M', { size: 14 })({ textPresets });
      return { ...font } as IWixStyleFont;
    },
  });

const subtitleFont = (key: string) =>
  createStylesParam(key, {
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      const { value: _, ...font } = wixFontParam('Body-S', { size: 12 })({ textPresets });
      return { ...font } as IWixStyleFont;
    },
  });

const textColor = (key: string, isActive: boolean) =>
  createStylesParam(key, {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam(isActive ? 'color-1' : 'color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(isActive ? CssColor.WstPrimaryBackgroundColor : CssColor.WstParagraph1Color),
    },
  });

const backgroundColor = (key: string, isActive: boolean) =>
  createStylesParam(key, {
    type: StyleParamType.Color,
    getDefaultValue: isActive ? wixColorParam('color-8') : wixColorParam('color-1', 0),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: isActive ? CssColor.WstLinksAndActionsColor : CssColor.WstPrimaryBackgroundColor,
        opacity: isActive ? 1 : 0,
      },
    },
  });

const cornerRadius = (key: string, defaultValue = 6) =>
  createStylesParam(key, {
    type: StyleParamType.Number,
    getDefaultValue: () => defaultValue,
    metadata: { type: StyleType.borderRadius, defaultValue: { type: 'static', value: defaultValue } },
  });

const borderWidth = (key: string, defaultValue = 1) =>
  createStylesParam(key, {
    type: StyleParamType.Number,
    getDefaultValue: () => defaultValue,
    metadata: { type: StyleType.borderWidth, defaultValue: { type: 'static', value: defaultValue } },
  });

const borderColor = (key: string) =>
  createStylesParam(key, {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.2),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: { type: 'static', preset: CssColor.WstSystemLine1Color, opacity: 0.2 },
    },
  });

export const tabsStylesParams = {
  tabsGroupBackgroundColor: createStylesParam('tabs-group-background-color', {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1', 0),
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: CssColor.WstPrimaryBackgroundColor, opacity: 0 },
    },
  }),
  tabsGroupCornerRadius: cornerRadius('tabs-group-corner-radius', 0),
  tabsGroupBorderWidth: borderWidth('tabs-group-border-width', 1),
  tabsGroupBorderColor: borderColor('tabs-group-border-color'),

  tabActiveDefaultTitleFont: titleFont('tab-active-default-title-font'),
  tabActiveDefaultTitleColor: textColor('tab-active-default-title-color', true),
  tabActiveDefaultSubtitleFont: subtitleFont('tab-active-default-subtitle-font'),
  tabActiveDefaultSubtitleColor: textColor('tab-active-default-subtitle-color', true),
  tabActiveDefaultBackgroundColor: backgroundColor('tab-active-default-background-color', true),
  tabActiveDefaultCornerRadiusTopLeft: cornerRadius('tab-active-default-corner-radius-top-left'),
  tabActiveDefaultCornerRadiusTopRight: cornerRadius('tab-active-default-corner-radius-top-right'),
  tabActiveDefaultCornerRadiusBottomLeft: cornerRadius('tab-active-default-corner-radius-bottom-left'),
  tabActiveDefaultCornerRadiusBottomRight: cornerRadius('tab-active-default-corner-radius-bottom-right'),
  tabActiveDefaultBorderWidth: borderWidth('tab-active-default-border-width'),
  tabActiveDefaultBorderColor: borderColor('tab-active-default-border-color'),

  tabActiveHoverTitleFont: titleFont('tab-active-hover-title-font'),
  tabActiveHoverTitleColor: textColor('tab-active-hover-title-color', true),
  tabActiveHoverSubtitleFont: subtitleFont('tab-active-hover-subtitle-font'),
  tabActiveHoverSubtitleColor: textColor('tab-active-hover-subtitle-color', true),
  tabActiveHoverBackgroundColor: backgroundColor('tab-active-hover-background-color', true),
  tabActiveHoverCornerRadiusTopLeft: cornerRadius('tab-active-hover-corner-radius-top-left'),
  tabActiveHoverCornerRadiusTopRight: cornerRadius('tab-active-hover-corner-radius-top-right'),
  tabActiveHoverCornerRadiusBottomLeft: cornerRadius('tab-active-hover-corner-radius-bottom-left'),
  tabActiveHoverCornerRadiusBottomRight: cornerRadius('tab-active-hover-corner-radius-bottom-right'),
  tabActiveHoverBorderWidth: borderWidth('tab-active-hover-border-width'),
  tabActiveHoverBorderColor: borderColor('tab-active-hover-border-color'),

  tabInactiveDefaultTitleFont: titleFont('tab-inactive-default-title-font'),
  tabInactiveDefaultTitleColor: textColor('tab-inactive-default-title-color', false),
  tabInactiveDefaultSubtitleFont: subtitleFont('tab-inactive-default-subtitle-font'),
  tabInactiveDefaultSubtitleColor: textColor('tab-inactive-default-subtitle-color', false),
  tabInactiveDefaultBackgroundColor: backgroundColor('tab-inactive-default-background-color', false),
  tabInactiveDefaultCornerRadiusTopLeft: cornerRadius('tab-inactive-default-corner-radius-top-left'),
  tabInactiveDefaultCornerRadiusTopRight: cornerRadius('tab-inactive-default-corner-radius-top-right'),
  tabInactiveDefaultCornerRadiusBottomLeft: cornerRadius('tab-inactive-default-corner-radius-bottom-left'),
  tabInactiveDefaultCornerRadiusBottomRight: cornerRadius('tab-inactive-default-corner-radius-bottom-right'),
  tabInactiveDefaultBorderWidth: borderWidth('tab-inactive-default-border-width'),
  tabInactiveDefaultBorderColor: borderColor('tab-inactive-default-border-color'),

  tabInactiveHoverTitleFont: titleFont('tab-inactive-hover-title-font'),
  tabInactiveHoverTitleColor: textColor('tab-inactive-hover-title-color', false),
  tabInactiveHoverSubtitleFont: subtitleFont('tab-inactive-hover-subtitle-font'),
  tabInactiveHoverSubtitleColor: textColor('tab-inactive-hover-subtitle-color', false),
  tabInactiveHoverBackgroundColor: backgroundColor('tab-inactive-hover-background-color', false),
  tabInactiveHoverCornerRadiusTopLeft: cornerRadius('tab-inactive-hover-corner-radius-top-left'),
  tabInactiveHoverCornerRadiusTopRight: cornerRadius('tab-inactive-hover-corner-radius-top-right'),
  tabInactiveHoverCornerRadiusBottomLeft: cornerRadius('tab-inactive-hover-corner-radius-bottom-left'),
  tabInactiveHoverCornerRadiusBottomRight: cornerRadius('tab-inactive-hover-corner-radius-bottom-right'),
  tabInactiveHoverBorderWidth: borderWidth('tab-inactive-hover-border-width'),
  tabInactiveHoverBorderColor: borderColor('tab-inactive-hover-border-color'),
};
